// A day's lineup: which loop plays in each of its slots (2 647 at 125 BPM, 1 524 at 72 BPM) (SPEC §3, §4). Frozen at T−60 min
// and served as one compact document; loop note data is fetched in batches ahead of the playhead.

import { z } from "zod";
import { clockFor, type Bpm } from "./clock";

/** Most slots a day has (the 125 BPM stream). */
const MAX_SLOTS = clockFor(125).SLOTS_PER_DAY;

export const lineupEntrySchema = z.object({
  slot: z
    .number()
    .int()
    .min(0)
    .max(MAX_SLOTS - 1),
  loopId: z.string(),
  /** Author's nickname; null for a former contributor (deleted account). */
  author: z.string().nullable(),
  title: z.string().nullable(),
  kind: z.enum(["human", "filler"]),
  /** Wizard-made notes ÷ all notes (0–1). Fillers are 1. */
  wizardShare: z.number().min(0).max(1),
  /** Loudness-normalisation playback gain (linear). */
  gain: z.number().min(0).max(8),
  /** Encore: this is the song's (repeat + 1)-th play in a row (see repeats.ts). */
  repeat: z.number().int().min(1).max(4).optional(),
});
export type LineupEntry = z.infer<typeof lineupEntrySchema>;

export interface Lineup {
  /** The stream (tempo) this lineup plays in. */
  bpm: Bpm;
  day: number;
  date: string;
  /** Epoch ms when it froze (null: not frozen yet / preview). */
  frozenAt: number | null;
  /** Exactly the stream's SLOTS_PER_DAY entries, indexed by slot. */
  entries: LineupEntry[];
  /** Monotonic version: bumps with every patch (reports, deleted accounts). */
  version: number;
}

/** A live change to a frozen lineup (a hidden loop replaced by a filler, or an encore that moves
 *  a few entries): `entry`, plus `entries` when several slots change in one version. */
export interface LineupPatch {
  bpm: Bpm;
  day: number;
  version: number;
  entry: LineupEntry;
  entries?: LineupEntry[];
  reason: string;
}

export function applyPatch(l: Lineup, p: LineupPatch): Lineup {
  if (p.day !== l.day || p.bpm !== l.bpm || p.version <= l.version) return l;
  const entries = l.entries.slice();
  for (const e of p.entries ?? [p.entry]) entries[e.slot] = e;
  return { ...l, entries, version: p.version };
}

/** Display string for the Wizard mark (SPEC §7). */
export function wizardLabel(e: Pick<LineupEntry, "kind" | "wizardShare">): string | null {
  if (e.kind === "filler") return "Wizard filler";
  if (e.wizardShare <= 0) return null;
  return `${Math.round(e.wizardShare * 100)} % Wizard`;
}
